// Shared status → Ant tag colour mapping, reused across the list and detail views.
import { ExclamationCircleFilled, PauseCircleFilled } from "@ant-design/icons";
import { Tag, Tooltip } from "antd";
import React from "react";

import { fmtText } from "../format";

const STATUS_COLORS: Record<string, string> = {
  pending: "default",
  in_progress: "processing",
  partial: "warning",
  completed: "success",
  cancelled: "error",
  error: "error",
  held: "gold", // a visible wait — distinct from the red `error`/`cancelled`
};

// "in_progress" -> "In progress". Tags elsewhere in OMS are sentence-cased; a raw lowercase
// value next to them reads as a different kind of thing.
const humanize = (v: string) => {
  const spaced = v.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const StatusTag: React.FC<{
  status?: string | null;
  // The reason to surface on hover — shown for `error` (a failure) and `held` (a wait). Typed
  // `unknown` deliberately: it is provider-derived text, so it is coerced (`fmtText`) rather than
  // trusted to be a string. An object here would throw inside the Tooltip and blank the page.
  tooltip?: unknown;
  // Classifies an `error` status: `"invoice"` is a payment/invoice HOLD (the Zoho invoice isn't fully
  // paid) — a wait dressed as an error. Ops asked for these to read distinctly (amber "Invoice
  // Error") from a real red failure, which they otherwise can't tell apart.
  errorKind?: string | null;
}> = ({ status, tooltip, errorKind }) => {
  if (!status) return <Tag>—</Tag>;
  const isInvoiceError = status === "error" && errorKind === "invoice";
  const isError = status === "error" && !isInvoiceError;
  const isHeld = status === "held";
  const emphasised = isError || isHeld || isInvoiceError;
  const color = isInvoiceError ? "gold" : STATUS_COLORS[status] ?? "default";
  const tag = (
    <Tag
      color={color}
      icon={
        isError ? (
          <ExclamationCircleFilled />
        ) : isInvoiceError || isHeld ? (
          <PauseCircleFilled />
        ) : undefined
      }
      style={emphasised ? { fontWeight: 600 } : undefined}
    >
      {isInvoiceError ? "Invoice Error" : humanize(status)}
    </Tag>
  );
  const tooltipText = fmtText(tooltip);
  if (emphasised && tooltipText) {
    return (
      <Tooltip
        title={tooltipText}
        overlayStyle={{ maxWidth: 480 }}
        overlayInnerStyle={{ whiteSpace: "pre-line" }}
      >
        <span style={{ cursor: "help" }}>{tag}</span>
      </Tooltip>
    );
  }
  return tag;
};

export default StatusTag;
